import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Table, Button, TextInput, Select, Group, Badge, Text,
  Modal, Card, SimpleGrid, Stack, ActionIcon,
  Loader, Alert, Paper, Checkbox, Menu
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconUpload, IconFileDescription,
  IconSearch, IconTrash, IconEye,
  IconAlertCircle, IconClock, IconX,
  IconCpu, IconFile, IconFileInvoice, IconPlayerPlay, IconDotsVertical,
  IconArrowUpRight, IconArrowDownRight, IconWallet, IconCurrencyFrank,
  IconPhoto, IconCalendarMonth, IconReportMoney
} from '@tabler/icons-react';
import { Dropzone } from '@mantine/dropzone';
import { DOCUMENT_TYPES, STATUS_OPTIONS, APPROVAL_OPTIONS, TYPE_ICONS, TYPE_COLORS, STATUS_COLORS, STATUS_ICONS } from './accounting-constants';
import apiService from '../services/api';
import { formatPrice } from '../utils/formatPrice';

export default function AdminAccounting() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [filters, setFilters] = useState({ status: '', document_type: '', is_approved: '', tag: '', search: '' });
  const [selectedIds, setSelectedIds] = useState([]);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [typeChangeDoc, setTypeChangeDoc] = useState(null);
  const [typeChangeValue, setTypeChangeValue] = useState('invoice');
  const pollRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    try {
      const p = {};
      if (filters.status) p.status = filters.status;
      if (filters.document_type) p.document_type = filters.document_type;
      if (filters.is_approved) p.is_approved = filters.is_approved;
      if (filters.tag) p.tag = filters.tag;
      if (filters.search) p.search = filters.search;
      const data = await apiService.getAccountingDocuments(p);
      setDocuments(data.documents || []);
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try { setStats(await apiService.getAccountingStats()); } catch (_) {}
  }, []);

  useEffect(() => { fetchDocs(); fetchStats(); }, [fetchDocs, fetchStats]);

  const hasProcessing = useMemo(() =>
    documents.some(d => d.processing_status === 'processing' || d.processing_status === 'queued'),
    [documents]
  );

  useEffect(() => {
    if (hasProcessing) {
      pollRef.current = setInterval(fetchDocs, 3000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [hasProcessing, fetchDocs]);

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of uploadFiles) fd.append('files', f.file);
      fd.append('document_types', JSON.stringify(uploadFiles.map(f => f.type)));
      fd.append('auto_process', 'true');
      await apiService.uploadAccountingDocument(fd);
      notifications.show({ title: 'Uploaded', message: `${uploadFiles.length} document(s) uploaded`, color: 'green' });
      setUploadOpen(false);
      setUploadFiles([]);
      fetchDocs();
      fetchStats();
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    } finally {
      setUploading(false);
    }
  };

  const handleProcess = async (id) => {
    try {
      await apiService.processAccountingDocument(id);
      notifications.show({ title: 'Processing', message: 'AI extraction started', color: 'blue' });
      fetchDocs();
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await apiService.deleteAccountingDocument(id);
      setSelectedIds(prev => prev.filter(sid => sid !== id));
      notifications.show({ title: 'Deleted', color: 'green' });
      fetchDocs();
      fetchStats();
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    }
  };

  const handleBatchProcess = async () => {
    if (selectedIds.length === 0) return;
    setBatchProcessing(true);
    try {
      await apiService.batchProcessAccountingDocuments(selectedIds);
      notifications.show({ title: 'Processing', message: `${selectedIds.length} document(s) queued`, color: 'blue' });
      setSelectedIds([]);
      fetchDocs();
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    } finally {
      setBatchProcessing(false);
    }
  };

  const handleSelectOne = (id, checked) => {
    setSelectedIds(prev =>
      checked ? [...prev, id] : prev.filter(sid => sid !== id)
    );
  };

  const handleRemoveFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleChangeType = async () => {
    if (!typeChangeDoc) return;
    try {
      await apiService.updateAccountingDocumentType(typeChangeDoc.id, typeChangeValue);
      notifications.show({ title: 'Updated', message: 'Document type changed', color: 'green' });
      setTypeChangeDoc(null);
      fetchDocs();
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const monthGroups = useMemo(() => {
    const groups = {};
    for (const doc of documents) {
      const d = new Date(doc.document_date || doc.created_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      if (!groups[key]) groups[key] = { label, key, docs: [] };
      groups[key].docs.push(doc);
    }
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([, val]) => val);
  }, [documents]);

  const byStatus = stats?.by_status || [];
  const f = stats?.finances || {};
  const netBalance = (Number(f.total_revenue) || 0) - (Number(f.total_expenses) || 0);


  const statCards = [
    { label: 'Total Documents', value: stats?.total_documents ?? '-', color: 'blue' },
    { label: 'Pending Review', value: byStatus.find(s => s.processing_status === 'needs_review')?.count || 0, color: 'orange' },
    { label: 'Processing', value: byStatus.find(s => s.processing_status === 'processing')?.count || 0, color: 'yellow' },
    { label: 'Failed', value: byStatus.find(s => s.processing_status === 'failed')?.count || 0, color: 'red' }
  ];

  return (
    <Paper p="md">
      <Group justify="space-between" mb="lg">
        <Text size="xl" fw={700}>Bookkeeping</Text>
        <Group gap="sm">
          <Button
            component={Link}
            to="/dashboard/accounting/vat-report"
            variant="light"
            leftSection={<IconReportMoney size={16} />}
          >
            VAT Report
          </Button>
          <Button leftSection={<IconUpload size={16} />} onClick={() => setUploadOpen(true)}>
            Upload Document
          </Button>
        </Group>
      </Group>

      <Text size="sm" fw={600} tt="uppercase" c="dimmed" mb="xs">Financial Overview</Text>
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="lg">
        <Card withBorder padding="md" radius="md">
          <Group gap="xs" mb={4}>
            <IconArrowUpRight size={16} color="var(--mantine-color-green-6)" />
            <Text size="xs" c="dimmed" tt="uppercase" fw={500}>Revenue</Text>
          </Group>
          <Text size="lg" fw={700} c="green">{formatPrice(f.total_revenue)}</Text>
        </Card>
        <Card withBorder padding="md" radius="md">
          <Group gap="xs" mb={4}>
            <IconArrowDownRight size={16} color="var(--mantine-color-red-6)" />
            <Text size="xs" c="dimmed" tt="uppercase" fw={500}>Expenses</Text>
          </Group>
          <Text size="lg" fw={700} c="red">{formatPrice(f.total_expenses)}</Text>
        </Card>
        <Card withBorder padding="md" radius="md">
          <Group gap="xs" mb={4}>
            <IconWallet size={16} color={netBalance >= 0 ? 'var(--mantine-color-green-6)' : 'var(--mantine-color-red-6)'} />
            <Text size="xs" c="dimmed" tt="uppercase" fw={500}>Net Balance</Text>
          </Group>
          <Text size="lg" fw={700} c={netBalance >= 0 ? 'green' : 'red'}>{formatPrice(netBalance)}</Text>
        </Card>
        <Card withBorder padding="md" radius="md">
          <Group gap="xs" mb={4}>
            <IconCurrencyFrank size={16} color="var(--mantine-color-blue-6)" />
            <Text size="xs" c="dimmed" tt="uppercase" fw={500}>Outstanding</Text>
          </Group>
          <Text size="lg" fw={700} c="blue">{formatPrice(f.outstanding_invoices)}</Text>
        </Card>
      </SimpleGrid>

      <Text size="sm" fw={600} tt="uppercase" c="dimmed" mb="xs">Document Status</Text>
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="lg">
        {statCards.map(c => (
          <Card key={c.label} withBorder padding="md" radius="md">
            <Text size="xs" c="dimmed" tt="uppercase" fw={500}>{c.label}</Text>
            <Text size="lg" fw={700} c={c.color}>{c.value}</Text>
          </Card>
        ))}
      </SimpleGrid>

      <Paper withBorder p="sm" mb="md">
        <Group gap="sm">
          <TextInput
            placeholder="Search counterparty, invoice..."
            leftSection={<IconSearch size={16} />}
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="All types"
            data={[{ value: '', label: 'All Types' }, ...DOCUMENT_TYPES]}
            value={filters.document_type}
            onChange={v => setFilters(f => ({ ...f, document_type: v }))}
            w={180}
            clearable
          />
          <Select
            placeholder="All statuses"
            data={STATUS_OPTIONS}
            value={filters.status}
            onChange={v => setFilters(f => ({ ...f, status: v }))}
            w={155}
            clearable
          />
          <Select
            placeholder="Approval"
            data={APPROVAL_OPTIONS}
            value={filters.is_approved}
            onChange={v => setFilters(f => ({ ...f, is_approved: v }))}
            w={155}
            clearable
          />
          <Select
            placeholder="All tags"
            data={[
              { value: 'vat', label: 'vat' },
              { value: 'capex', label: 'capex' },
              { value: 'opex', label: 'opex' }
            ]}
            value={filters.tag}
            onChange={v => setFilters(f => ({ ...f, tag: v }))}
            w={120}
            clearable
          />
        </Group>
      </Paper>

      {selectedIds.length > 0 && (
        <Paper withBorder p="sm" mb="md" bg="blue.0">
          <Group justify="space-between">
            <Text size="sm" fw={500}>{selectedIds.length} selected</Text>
            <Group gap="sm">
              <Button
                size="xs"
                color="cyan"
                leftSection={<IconPlayerPlay size={14} />}
                onClick={handleBatchProcess}
                loading={batchProcessing}
              >
                Process Selected
              </Button>
              <Button size="xs" variant="light" color="gray" onClick={() => setSelectedIds([])}>
                Clear
              </Button>
            </Group>
          </Group>
        </Paper>
      )}

      {loading ? (
        <Group justify="center" py="xl"><Loader /></Group>
      ) : documents.length === 0 ? (
        <Alert icon={<IconAlertCircle size={16} />} title="No documents">
          {filters.search || filters.document_type || filters.status || filters.is_approved
            ? 'No documents match your filters.'
            : 'Upload your first document to get started.'}
        </Alert>
      ) : (
        <Stack gap="lg">
          {monthGroups.map(group => {
            const m = stats?.monthly?.[group.key];
            return (
            <Paper key={group.key} withBorder p="sm" radius="md">
              <Group mb="sm" gap="xs" justify="space-between">
                <Group gap="xs">
                  <IconCalendarMonth size={18} color="var(--mantine-color-gray-6)" />
                  <Text fw={600} size="md">{group.label}</Text>
                  <Badge variant="light" size="sm">{group.docs.length} document{group.docs.length !== 1 ? 's' : ''}</Badge>
                </Group>
                {m && (
                  <Group gap="md">
                    <Text size="xs" c="dimmed" span>Revenue:</Text>
                    <Text size="sm" c="green" fw={500}>+ {formatPrice(m.revenue)}</Text>
                    <Text size="xs" c="dimmed" span>Expenses:</Text>
                    <Text size="sm" c="red" fw={500}>− {formatPrice(m.expenses)}</Text>
                    <Text size="xs" c="dimmed" span>Net:</Text>
                    <Text size="sm" fw={600} c={m.net >= 0 ? 'green' : 'red'}>
                      {m.net >= 0 ? '+' : ''} {formatPrice(m.net)}
                    </Text>
                  </Group>
                )}
              </Group>
              <Table.ScrollContainer minWidth={400}>
              <Table striped highlightOnHover withTableBorder caption={undefined}>
                <caption className="sr-only">{`Bookkeeping documents for ${group.label}`}</caption>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th scope="col" w={32}>
                      <Checkbox
                        size="xs"
                        checked={group.docs.length > 0 && group.docs.every(d => selectedIds.includes(d.id))}
                        indeterminate={group.docs.some(d => selectedIds.includes(d.id)) && !group.docs.every(d => selectedIds.includes(d.id))}
                        onChange={e => {
                          if (e.currentTarget.checked) setSelectedIds(prev => [...new Set([...prev, ...group.docs.map(d => d.id)])]);
                          else setSelectedIds(prev => prev.filter(id => !group.docs.some(d => d.id === id)));
                        }}
                        aria-label={`Select all documents for ${group.label}`}
                      />
                    </Table.Th>
                    <Table.Th scope="col">Document</Table.Th>
                    <Table.Th scope="col" ta="right" w={110}>Invoice #</Table.Th>
                    <Table.Th scope="col" ta="right" w={120}>Total</Table.Th>
                    <Table.Th scope="col" ta="right" w={210}>Status</Table.Th>
                    <Table.Th scope="col" w={50}>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {group.docs.map(doc => {
                    const TI = TYPE_ICONS[doc.document_type] || IconFileDescription;
                    const busy = doc.processing_status === 'processing' || doc.processing_status === 'queued';
                    const p = doc.processing_status;
                    const selectable = p === 'pending' || p === 'failed';
                    const typeColor = TYPE_COLORS[doc.document_type] || 'gray';
                    const isRejected = doc.is_approved === 0 || doc.is_approved === false;
                    const ProcIcon = STATUS_ICONS[doc.processing_status] || IconClock;
                    return (
                      <Table.Tr
                        key={doc.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/dashboard/accounting/${doc.id}`)}
                      >
                        <Table.Td onClick={e => e.stopPropagation()}>
                          <Checkbox
                            size="xs"
                            checked={selectedIds.includes(doc.id)}
                            onChange={e => handleSelectOne(doc.id, e.currentTarget.checked)}
                            disabled={!selectable}
                            aria-label={`Select ${doc.filename || 'document'}`}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Group gap="sm" wrap="nowrap">
                            <TI size={18} color={`var(--mantine-color-${typeColor}-6)`} style={{ flexShrink: 0 }} />
                            <div style={{ minWidth: 0 }}>
                              <Text size="sm" truncate>{doc.counterparty_name || 'Untitled'}</Text>
                              <Text size="xs" c="dimmed">
                                {doc.document_date
                                  ? new Date(doc.document_date).toLocaleDateString('en-GB')
                                  : '—'}
                              </Text>
                            </div>
                          </Group>
                        </Table.Td>
                        <Table.Td ta="right"><Text size="sm">{doc.invoice_number || '—'}</Text></Table.Td>
                        <Table.Td ta="right">
                          <Text size="sm" fw={500}>
                            {doc.total_amount != null
                              ? formatPrice(doc.total_amount)
                              : '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          <Group gap={4} wrap="nowrap" justify="flex-end">
                            <Badge
                              size="sm"
                              color={STATUS_COLORS[doc.processing_status] || 'gray'}
                              variant={doc.processing_status === 'completed' && doc.is_approved === null ? 'light' : 'filled'}
                              leftSection={busy ? <Loader size={10} /> : <ProcIcon size={11} />}
                            >
                              {doc.processing_status?.replace(/_/g, ' ')}
                            </Badge>
                            {doc.is_approved ? (
                              <Badge size="sm" color="green" variant="filled">Approved</Badge>
                            ) : isRejected ? (
                              <Badge size="sm" color="red" variant="filled">Rejected</Badge>
                            ) : doc.processing_status === 'completed' ? (
                              <Badge size="sm" color="yellow" variant="light">Pending</Badge>
                            ) : null}
                          </Group>
                        </Table.Td>
                        <Table.Td onClick={e => e.stopPropagation()}>
                          <Menu shadow="md" width={160}>
                            <Menu.Target>
                              <ActionIcon variant="subtle" color="gray" size="sm">
                                <IconDotsVertical size={14} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item leftSection={<IconEye size={14} />} onClick={() => navigate(`/dashboard/accounting/${doc.id}`)}>
                                View / Edit
                              </Menu.Item>
                              {!busy && doc.processing_status !== 'completed' && (
                                <Menu.Item leftSection={<IconCpu size={14} />} onClick={() => handleProcess(doc.id)}>
                                  Process with AI
                                </Menu.Item>
                              )}
                              <Menu.Item leftSection={<IconFileInvoice size={14} />} onClick={() => { setTypeChangeValue(doc.document_type); setTypeChangeDoc(doc); }}>
                                Change Type
                              </Menu.Item>
                              <Menu.Divider />
                              <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={() => handleDelete(doc.id)}>
                                Delete
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
              </Table.ScrollContainer>
            </Paper>
            );
          })}
        </Stack>
      )}

      <Modal opened={!!typeChangeDoc} onClose={() => setTypeChangeDoc(null)} title={typeChangeDoc ? `Change Type — ${typeChangeDoc.filename || typeChangeDoc.counterparty_name || 'document'}` : ''} size="sm">
        <Stack>
          <Select
            label="Document Type"
            data={DOCUMENT_TYPES}
            value={typeChangeValue}
            onChange={setTypeChangeValue}
            allowDeselect={false}
            aria-required="true"
          />
          <Group justify="flex-end">
            <Button variant="light" onClick={() => setTypeChangeDoc(null)}>Cancel</Button>
            <Button onClick={handleChangeType}>Save</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={uploadOpen} onClose={() => { setUploadOpen(false); setUploadFiles([]); }} title="Upload Accounting Documents" size="md">
        <Stack>
          <Dropzone
            onDrop={files => setUploadFiles(prev => [...prev, ...files.map(f => ({ file: f, type: 'invoice' }))])}
            maxSize={20 * 1024 * 1024}
            accept={['application/pdf', 'image/png', 'image/jpeg', 'image/webp', 'image/tiff']}
            maxFiles={10}
            multiple
          >
            <Group justify="center" gap="xl" mih={120} style={{ pointerEvents: 'none' }}>
              <Dropzone.Accept>
                <IconUpload size={40} stroke={1.5} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={40} stroke={1.5} color="var(--mantine-color-red-6)" />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconPhoto size={40} stroke={1.5} />
              </Dropzone.Idle>
              <div>
                <Text size="sm" inline>Click or drag files here</Text>
                <Text size="xs" c="dimmed" mt={4}>PDF, PNG, JPG, WEBP — up to 10 files, 20MB each</Text>
              </div>
            </Group>
          </Dropzone>

          {uploadFiles.length > 0 && (
            <Stack gap="xs">
              <Text size="sm" fw={500}>{uploadFiles.length} file(s) selected</Text>
              {uploadFiles.map((f, i) => (
                <Group key={`${f.file.name}-${i}`} gap="sm" wrap="nowrap">
                  <IconFile size={16} stroke={1.5} />
                  <Text size="sm" style={{ flex: 1 }} truncate>{f.file.name}</Text>
                  <Select
                    size="xs"
                    style={{ width: 140 }}
                    data={DOCUMENT_TYPES}
                    value={f.type}
                    onChange={v => {
                      setUploadFiles(prev => prev.map((pf, pi) => pi === i ? { ...pf, type: v } : pf));
                    }}
                  />
                  <Text size="xs" c="dimmed">{formatSize(f.file.size)}</Text>
                  <ActionIcon size="sm" color="red" variant="subtle" onClick={() => handleRemoveFile(i)}>
                    <IconX size={14} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
          )}

          <Button onClick={handleUpload} loading={uploading} disabled={uploadFiles.length === 0} fullWidth>
            Upload{uploadFiles.length > 0 ? ` (${uploadFiles.length} file(s))` : ''}
          </Button>
        </Stack>
      </Modal>
    </Paper>
  );
}
